import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Loader from '../../../shared/components/ui/Loader';
import {
  EmptyState,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead
} from '../../../shared/components/ui/primitives';
import {
  decideSiteWalletExpense,
  getSiteWalletApprovals
} from '../../services/api/implementation/impl';
import {
  BillLinks,
  DecisionButtons,
  HolderName,
  NoteModal,
  errorMessage,
  money,
  shortDate
} from './siteWalletUi';

/**
 * Expenses waiting for a decision. An Accountant gets the ones on their
 * projects (the server lists only those waiting on them); the builder side
 * gets all of them, including projects with no Accountant.
 *
 * Nobody approves their own expense, so those rows have no buttons.
 */
const ExpenseApprovalsPanel = ({ role }) => {
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(null);

  const approvals = useQuery({
    queryKey: ['siteWalletApprovals'],
    queryFn: getSiteWalletApprovals
  });
  const rows = approvals.data ?? [];

  const decide = useMutation({
    mutationFn: ({ id, ...payload }) => decideSiteWalletExpense(id, payload),
    onSuccess: (_r, vars) => {
      queryClient.invalidateQueries({ queryKey: ['siteWalletApprovals'] });
      queryClient.invalidateQueries({ queryKey: ['siteWallets'] });
      queryClient.invalidateQueries({ queryKey: ['siteWallet'] });
      queryClient.invalidateQueries({ queryKey: ['siteWalletReimbursements'] });
      setRejecting(null);
      toast.success(vars.decision === 'REJECT' ? 'Expense rejected' : 'Expense approved');
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not record the decision'))
  });

  if (approvals.isLoading) return <Loader />;
  if (!rows.length) return <EmptyState title="Nothing waiting for you" />;

  return (
    <>
      <TableCard>
        <Table>
          <THead>
            <tr>
              <Th>Site engineer</Th>
              <Th>Project · Category</Th>
              <Th align="right">Amount</Th>
              <Th>Spent on</Th>
              <Th>Bill</Th>
              <Th>Note</Th>
              <Th>Waiting on</Th>
              <Th />
            </tr>
          </THead>
          <TBody>
            {rows.map((e) => {
              const own = e.user_id === role.userId;
              return (
                <tr key={e.id} className={own ? 'opacity-60' : ''}>
                  <Td>
                    <HolderName user={e.user} />
                  </Td>
                  <Td>
                    <div className="text-gray-900">{e.project_name ?? '—'}</div>
                    <div className="text-sm text-gray-500">{e.category_name ?? '—'}</div>
                  </Td>
                  <Td align="right" numeric>
                    <div className="font-semibold">{money(e.amount)}</div>
                    {Number(e.own_amount) > 0 && (
                      <div className="text-sm text-gray-500">
                        {money(e.wallet_amount)} wallet · {money(e.own_amount)} own pocket
                      </div>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap">{shortDate(e.spent_on)}</Td>
                  <Td>
                    <BillLinks urls={e.bill_urls} />
                  </Td>
                  <Td className="max-w-xs text-sm text-gray-700">{e.note || '—'}</Td>
                  <Td className="text-sm text-gray-600">
                    {e.approver_role === 'ACCOUNTS' ? (e.approver_name ?? 'Accountant') : 'Builder'}
                  </Td>
                  <Td>
                    {own ? (
                      <span className="text-sm text-gray-500">Your own expense</span>
                    ) : (
                      <DecisionButtons
                        disabled={decide.isPending}
                        onApprove={() => decide.mutate({ id: e.id, decision: 'APPROVE' })}
                        onReject={() => setRejecting(e)}
                      />
                    )}
                  </Td>
                </tr>
              );
            })}
          </TBody>
        </Table>
      </TableCard>
      {rejecting && (
        <NoteModal
          title={`Reject ${money(rejecting.amount)} from ${rejecting.user?.name ?? 'site engineer'}`}
          label="Reason"
          placeholder="The site engineer sees this"
          confirm="Reject expense"
          danger
          busy={decide.isPending}
          onClose={() => setRejecting(null)}
          onSubmit={(reason) => decide.mutate({ id: rejecting.id, decision: 'REJECT', reason })}
        />
      )}
    </>
  );
};

export default ExpenseApprovalsPanel;
